import { useEffect, useState } from "react";
import { api, resolveImg, fmtRON, apiError } from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { useAuth } from "@/context/AuthContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";
import {
  Wallet, CalendarDays, Home, TrendingUp, Star, Plus, Upload, Loader2, X, Trash2, Image as ImageIcon,
} from "lucide-react";

const REGIONS = ["Bran-Moieciu", "Poiana Brașov", "Sinaia", "Zărnești", "Transfăgărășan", "Apuseni", "Maramureș", "Bucegi"];
const CATS = [
  { id: "a-frame", label: "A-Frame" }, { id: "chalet", label: "Chalet de lux" },
  { id: "ciubar", label: "Ciubăr & Saună" }, { id: "priveliste", label: "Cu priveliște" },
  { id: "cabana", label: "Cabană rustică" },
];
const AMENITIES = ["Șemineu pe lemne", "Ciubăr încălzit", "Saună finlandeză", "Wi-Fi Starlink",
  "Grătar exterior", "Priveliște montană", "Parcare gratuită", "Bucătărie complet utilată"];

function StatCard({ icon: Icon, label, value, accent }) {
  return (
    <div className="rounded-2xl border border-border/60 bg-card p-5">
      <div className={`h-10 w-10 rounded-xl flex items-center justify-center mb-3 ${accent ? "bg-accent/15 text-accent" : "bg-primary/10 text-primary"}`}>
        <Icon className="h-5 w-5" />
      </div>
      <p className="text-2xl font-semibold">{value}</p>
      <p className="text-sm text-muted-foreground">{label}</p>
    </div>
  );
}

function ListingForm({ onCreated, onClose }) {
  const [form, setForm] = useState({
    title: "", description: "", region: REGIONS[0], location: "", category: "a-frame",
    price_per_night: 400, max_guests: 4, bedrooms: 2, beds: 2, baths: 1,
  });
  const [amenities, setAmenities] = useState([AMENITIES[0], AMENITIES[5]]);
  const [images, setImages] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleAmenity = (a) => setAmenities((prev) => prev.includes(a) ? prev.filter((x) => x !== a) : [...prev, a]);

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setImages((prev) => [...prev, data.url]);
      }
      toast.success("Fotografii încărcate!");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally { setUploading(false); }
  };

  const submit = async () => {
    if (!form.title || !form.location) return toast.error("Completează titlul și locația");
    setSaving(true);
    try {
      const { data } = await api.post("/properties", {
        ...form,
        price_per_night: Number(form.price_per_night), max_guests: Number(form.max_guests),
        bedrooms: Number(form.bedrooms), beds: Number(form.beds), baths: Number(form.baths),
        amenities, images,
      });
      toast.success("Anunț publicat cu succes!");
      onCreated(data);
      onClose();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-4">
      <div className="space-y-1.5">
        <Label>Titlu anunț</Label>
        <Input value={form.title} onChange={(e) => set("title", e.target.value)} data-testid="listing-title-input"
               placeholder="Cabană A-Frame în pădure" />
      </div>
      <div className="space-y-1.5">
        <Label>Descriere</Label>
        <Textarea rows={3} value={form.description} onChange={(e) => set("description", e.target.value)}
                  data-testid="listing-description-input" placeholder="Descrie experiența oaspeților..." />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label>Regiune</Label>
          <select value={form.region} onChange={(e) => set("region", e.target.value)} data-testid="listing-region-select"
                  className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm">
            {REGIONS.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
        </div>
        <div className="space-y-1.5">
          <Label>Localitate</Label>
          <Input value={form.location} onChange={(e) => set("location", e.target.value)} data-testid="listing-location-input"
                 placeholder="Bran, Brașov" />
        </div>
      </div>
      <div className="space-y-1.5">
        <Label>Categorie</Label>
        <div className="flex flex-wrap gap-2">
          {CATS.map((c) => (
            <button key={c.id} onClick={() => set("category", c.id)} type="button"
                    className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${form.category === c.id ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-muted"}`}>
              {c.label}
            </button>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="space-y-1.5"><Label>Preț/noapte (lei)</Label>
          <Input type="number" value={form.price_per_night} onChange={(e) => set("price_per_night", e.target.value)} data-testid="listing-price-input" /></div>
        <div className="space-y-1.5"><Label>Oaspeți</Label>
          <Input type="number" value={form.max_guests} onChange={(e) => set("max_guests", e.target.value)} /></div>
        <div className="space-y-1.5"><Label>Dormitoare</Label>
          <Input type="number" value={form.bedrooms} onChange={(e) => set("bedrooms", e.target.value)} /></div>
        <div className="space-y-1.5"><Label>Băi</Label>
          <Input type="number" value={form.baths} onChange={(e) => set("baths", e.target.value)} /></div>
      </div>

      <div className="space-y-1.5">
        <Label>Facilități</Label>
        <div className="flex flex-wrap gap-2">
          {AMENITIES.map((a) => (
            <button key={a} type="button" onClick={() => toggleAmenity(a)}
                    className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${amenities.includes(a) ? "bg-accent/15 text-accent border-accent/40" : "border-border hover:bg-muted"}`}>
              {a}
            </button>
          ))}
        </div>
      </div>

      <div className="space-y-1.5">
        <Label>Fotografii</Label>
        <div className="flex flex-wrap gap-3">
          {images.map((im, i) => (
            <div key={i} className="relative h-20 w-20 rounded-lg overflow-hidden">
              <img src={resolveImg(im)} alt="" className="h-full w-full object-cover" />
              <button onClick={() => setImages(images.filter((_, x) => x !== i))}
                      className="absolute top-0.5 right-0.5 h-5 w-5 rounded-full bg-black/60 flex items-center justify-center">
                <X className="h-3 w-3 text-white" />
              </button>
            </div>
          ))}
          <label className="h-20 w-20 rounded-lg border-2 border-dashed border-border flex items-center justify-center cursor-pointer hover:bg-muted transition-colors" data-testid="listing-upload-label">
            {uploading ? <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /> : <Upload className="h-5 w-5 text-muted-foreground" />}
            <input type="file" accept="image/*" multiple className="hidden" onChange={handleUpload} data-testid="listing-image-input" />
          </label>
        </div>
      </div>

      <Button onClick={submit} disabled={saving} data-testid="add-listing-submit-button"
              className="w-full h-11 bg-accent hover:bg-accent/90 text-accent-foreground rounded-full">
        {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />} Publică anunțul
      </Button>
    </div>
  );
}

export default function HostDashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [properties, setProperties] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [formOpen, setFormOpen] = useState(false);

  const loadAll = async () => {
    const [s, p, b] = await Promise.all([
      api.get("/host/stats"), api.get("/host/properties"), api.get("/host/bookings"),
    ]);
    setStats(s.data); setProperties(p.data); setBookings(b.data);
  };

  useEffect(() => { loadAll(); }, []);

  const deleteProperty = async (id) => {
    try {
      await api.delete(`/properties/${id}`);
      setProperties((p) => p.filter((x) => x.id !== id));
      toast.success("Anunț șters");
      loadAll();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  // Build a simple calendar of booked date ranges
  const bookedRanges = bookings.filter((b) => b.payment_status === "paid").map((b) => ({
    title: b.property_title, ci: b.check_in, co: b.check_out, guest: b.guest_name,
  }));

  return (
    <div className="App">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <p className="overline text-accent mb-1">Panou gazdă</p>
            <h1 className="font-heading text-4xl font-semibold tracking-tight">Bună, {user?.name?.split(" ")[0]}!</h1>
          </div>
          <Dialog open={formOpen} onOpenChange={setFormOpen}>
            <DialogTrigger asChild>
              <Button data-testid="open-add-listing-button" className="rounded-full bg-primary hover:bg-primary/90 gap-2">
                <Plus className="h-4 w-4" /> Adaugă anunț
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle className="font-heading text-2xl">Anunț nou</DialogTitle></DialogHeader>
              <ListingForm onCreated={() => loadAll()} onClose={() => setFormOpen(false)} />
            </DialogContent>
          </Dialog>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <StatCard icon={Wallet} label="Încasări totale" value={stats ? fmtRON(stats.total_earnings) : "—"} accent />
          <StatCard icon={CalendarDays} label="Rezervări active" value={stats?.active_bookings ?? "—"} />
          <StatCard icon={Home} label="Anunțuri" value={stats?.properties ?? "—"} />
          <StatCard icon={Star} label="Evaluare medie" value={stats?.avg_rating ?? "—"} />
        </div>

        <Tabs defaultValue="calendar">
          <TabsList className="mb-6">
            <TabsTrigger value="calendar" data-testid="host-tab-calendar">Calendar</TabsTrigger>
            <TabsTrigger value="listings" data-testid="host-tab-listings">Anunțuri</TabsTrigger>
            <TabsTrigger value="bookings" data-testid="host-tab-bookings">Rezervări</TabsTrigger>
            <TabsTrigger value="earnings" data-testid="host-tab-earnings">Încasări</TabsTrigger>
          </TabsList>

          {/* Calendar */}
          <TabsContent value="calendar">
            <div className="rounded-2xl border border-border/60 bg-card p-6">
              <h3 className="font-heading text-xl font-semibold mb-4 flex items-center gap-2">
                <CalendarDays className="h-5 w-5 text-accent" /> Perioade rezervate
              </h3>
              {bookedRanges.length === 0 ? (
                <p className="text-muted-foreground text-sm py-8 text-center">Nicio perioadă rezervată încă.</p>
              ) : (
                <div className="space-y-3">
                  {bookedRanges.map((r, i) => (
                    <div key={i} className="flex items-center gap-4 rounded-xl bg-muted/50 px-4 py-3" data-testid={`calendar-range-${i}`}>
                      <div className="h-10 w-1.5 rounded-full bg-accent" />
                      <div className="flex-1">
                        <p className="font-medium text-sm">{r.title}</p>
                        <p className="text-xs text-muted-foreground font-mono">{r.ci} → {r.co}</p>
                      </div>
                      <Badge variant="secondary">{r.guest}</Badge>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          {/* Listings */}
          <TabsContent value="listings">
            {properties.length === 0 ? (
              <div className="text-center py-16 rounded-2xl border border-dashed border-border">
                <ImageIcon className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
                <p className="text-muted-foreground">Nu ai niciun anunț. Adaugă primul tău anunț!</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {properties.map((p) => (
                  <div key={p.id} className="rounded-2xl border border-border/60 bg-card overflow-hidden" data-testid={`host-listing-${p.id}`}>
                    <img src={resolveImg(p.images?.[0])} alt="" className="aspect-[4/3] w-full object-cover" />
                    <div className="p-4">
                      <h3 className="font-medium line-clamp-1">{p.title}</h3>
                      <p className="text-sm text-muted-foreground">{p.location}</p>
                      <div className="flex items-center justify-between mt-3">
                        <span className="font-semibold text-sm">{fmtRON(p.price_per_night)} / noapte</span>
                        <button onClick={() => deleteProperty(p.id)} data-testid={`delete-listing-${p.id}`}
                                className="text-muted-foreground hover:text-destructive transition-colors">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Bookings */}
          <TabsContent value="bookings">
            <div className="rounded-2xl border border-border/60 bg-card overflow-hidden">
              {bookings.length === 0 ? (
                <p className="text-muted-foreground text-sm py-12 text-center">Nicio rezervare încă.</p>
              ) : bookings.map((b) => (
                <div key={b.id} className="flex items-center gap-4 px-5 py-4 border-b border-border/60 last:border-0" data-testid={`booking-row-${b.id}`}>
                  <img src={resolveImg(b.property_image)} alt="" className="h-14 w-14 rounded-lg object-cover shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm truncate">{b.property_title}</p>
                    <p className="text-xs text-muted-foreground font-mono">{b.check_in} → {b.check_out} · {b.guest_name}</p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="font-semibold text-sm">{fmtRON(b.total_amount)}</p>
                    <Badge className={b.payment_status === "paid" ? "bg-primary text-primary-foreground" : "bg-muted text-foreground"}>
                      {b.payment_status === "paid" ? "Plătită" : "În așteptare"}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* Earnings */}
          <TabsContent value="earnings">
            <div className="rounded-2xl border border-border/60 bg-card p-6">
              <div className="flex items-center gap-2 mb-6">
                <TrendingUp className="h-5 w-5 text-accent" />
                <h3 className="font-heading text-xl font-semibold">Încasări lunare</h3>
              </div>
              {stats?.monthly?.length ? (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={stats.monthly}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                    <XAxis dataKey="month" stroke="hsl(var(--muted-foreground))" fontSize={12} />
                    <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} />
                    <Tooltip formatter={(v) => fmtRON(v)} contentStyle={{ borderRadius: 12, border: "1px solid hsl(var(--border))" }} />
                    <Bar dataKey="earnings" fill="hsl(var(--accent))" radius={[8, 8, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <p className="text-muted-foreground text-sm py-8 text-center">Nu există încasări încă.</p>
              )}
              <div className="grid grid-cols-2 gap-4 mt-6 pt-6 border-t border-border">
                <div>
                  <p className="text-sm text-muted-foreground">Total încasat</p>
                  <p className="text-2xl font-semibold text-accent">{stats ? fmtRON(stats.total_earnings) : "—"}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Total rezervări plătite</p>
                  <p className="text-2xl font-semibold">{stats?.total_bookings ?? "—"}</p>
                </div>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
